import type { IconProp } from "@fortawesome/fontawesome-svg-core"
import { faArrowRight } from "@fortawesome/free-solid-svg-icons"
import { faCar, faCarWrench, faXmark } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Avatar,
  Box,
  DialogTitle,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Typography,
  useTheme
} from "@mui/material"
import { AppContext } from "components/AppContextProvider"
import { allRoutes } from "generated/routes"
import { VehicleCheckStatus } from "helpers/Checks/ChecksStatusColor"
import moment from "moment"
import "moment/locale/sv"
import "moment/locale/da"
import {
  StyledBox,
  StyledBoxClose,
  StyledFontAwesomeIcon
} from "pages/vehicleregistry/pages/checks/InspectionsHistoryTable.styled"
import React, { useContext, useState } from "react"
import { useHistory } from "react-router-dom"
import { EventDialogProps } from "../BigCalendar"
import { statusColor, statusIcon, statusIconColor, statusLabel } from "../helpers/calendarStatusEvent"
import { EventType } from "../interfaces/calendarDialog.interface"
import { StyledBoxLink, StyledDialog } from "../style/CalendarDialog.styled"
import { InspectionButtons, ServiceButtons } from "./CalendarDialogActionButtons"
import { useTranslation } from "react-i18next"
import { InspectionBookingSidebar } from "pages/vehicleregistry/pages/checks/InspectionBookingSidebar/InspectionBookingSidebar"
import { fontAwesomeOldXlStyle } from "components/icons/fontAwesomeStyles"

export const EventDialog: React.FC<EventDialogProps> = ({ event, onClose, open, reloadCalendarEvents }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { toggleSideBar } = useContext(AppContext)
  const [clickedInspectionIds, setClickedInspectionIds] = useState<{ vehicleId: number; checkTemplateId: number } | null>(null)
  const routerHistory = useHistory()
  const handleClose = () => { onClose(null) }
  const jsonData = JSON.parse(event?.data)
  const eventType = event?.typeId

  const openInspectionSidebar = (event: any) => {
    const data = JSON.parse(event.data)
    setClickedInspectionIds({
      vehicleId: data.vehicleId,
      checkTemplateId: data.checkTemplateId
    })
    toggleSideBar("InspectionBookingSidebar", true)
  }

  return (
    <>

      {clickedInspectionIds && (
        <InspectionBookingSidebar
          vehicleId={clickedInspectionIds.vehicleId}
          checkTemplateId={clickedInspectionIds.checkTemplateId}
          onBooked={() => {
            reloadCalendarEvents && reloadCalendarEvents()
            setClickedInspectionIds(null)
          }}
          onUpdated={() => {
            reloadCalendarEvents && reloadCalendarEvents()
            setClickedInspectionIds(null)
          }}
        />
      )}
      <StyledDialog fullWidth onClose={handleClose} open={open}>
        <DialogTitle>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="flexstart"
            alignContent="center"
            gap={1.5}
            mb={1}
          >
            <FontAwesomeIcon
              style={{ ...fontAwesomeOldXlStyle, marginTop: "1px" }}
              color={statusIconColor(event?.typeId, event?.status)}
              icon={statusIcon(event?.typeId, event?.status) as IconProp}
            />
            <Box>
              <Typography variant="h4">{event?.title}</Typography>
              <Typography variant="bodySmall">{event?.subtitle}</Typography>
              {event?.start && (
                <Typography variant="bodySmall" color="textSecondary">
                  {event?.start && moment(event.start).format("HH:mm")}
                </Typography>
              )}
            </Box>
            <StyledBoxClose
              onClick={() => {
                handleClose()
              }}>
              <FontAwesomeIcon style={fontAwesomeOldXlStyle} color="grey" icon={faXmark} />
            </StyledBoxClose>
          </Box>
          <Typography variant="bodySmall" color="textSecondary">
            {t("global.status", "Status")}
          </Typography>
          <Typography fontWeight="500">
            {event?.typeId ? statusLabel(event?.typeId, event?.status || 0, false, t) : undefined}
          </Typography>
          {eventType === EventType.Service || eventType === EventType.Besiktning ? (
            <>
              {(event?.status === VehicleCheckStatus.Upcoming ||
                event?.status === VehicleCheckStatus.Late ||
                event?.status === VehicleCheckStatus.Waiting) &&
                jsonData.attendanceDate ? (
                <Typography variant="bodyMedium" color={statusColor(event?.typeId, event?.status)}>
                  {moment().isBefore(jsonData.attendanceDate) ? "Förfaller" : "Försenad"}{" "}
                  {moment(jsonData.attendanceDate).format("DD MMMM YYYY")}
                </Typography>
              ) : (
                <></>
              )}
              <Typography mt={2} variant="bodySmall" color="textSecondary">
                {t("global.description", "Description")}
              </Typography>
              {jsonData.inspectionBooking ? (
                <>
                  <Typography variant="bodySmall" fontWeight="500">
                    {t("deviation.location", "Location")}: {jsonData.inspectionBooking.CarWorkshop.Name}
                  </Typography>
                  <Typography variant="bodySmall" fontWeight="500">
                    {t("global.date", "Date")}: {moment(jsonData.inspectionBooking.DateTime).format("YYYY-MM-DD")}
                  </Typography>
                  <Typography variant="bodySmall" fontWeight="500">
                    {t("global.time", "Time")}: {moment(jsonData.inspectionBooking.DateTime).format("HH:mm")}
                  </Typography>
                </>
              ) : jsonData.carWorkshop ? (
                <>
                  <Typography variant="bodySmall" fontWeight="500">
                    {t("deviation.location", "Location")}: {jsonData.carWorkshop.Name}
                  </Typography>
                  <Typography variant="bodySmall" fontWeight="500">
                    {t("global.date", "Date")}: {moment(jsonData.approvedDate).format("YYYY-MM-DD")}
                  </Typography>
                  <Typography variant="bodySmall" fontWeight="500">
                    {t("global.time", "Time")}: {moment(jsonData.approvedDate).format("HH:mm")}
                  </Typography>
                </>
              ) : (
                <Typography variant="bodyMedium">{event?.description ? event?.description : "-"}</Typography>
              )}
            </>
          ) : (
            <>
              <Typography mt={2} variant="bodySmall" color="textSecondary">
                {t("global.description", "Description")}
              </Typography>
              <Typography variant="bodyMedium">{event?.description ? event?.description : "-"}</Typography>
            </>
          )}
        </DialogTitle>
        <Divider />
        <List disablePadding>
          <ListItem disableGutters disablePadding>
            <Grid container flexDirection="column">
              {eventType === EventType.Besiktning ? (
                <InspectionButtons
                  event={event}
                  openSidebar={openInspectionSidebar}
                  handleClose={handleClose}
                />
              ) : (
                <></>
              )}
              <StyledBoxLink
                onClick={() => {
                  routerHistory.push(allRoutes.VehicleregistryVehiclesDetails(event?.referenceId))
                }}>
                <StyledBox display="flex" justifyContent="space-between" alignItems={"center"}>
                  <Grid container gap={2}>
                    <FontAwesomeIcon style={fontAwesomeOldXlStyle} color="grey" icon={faCar} />
                    <Typography variant="h5" color={(theme.palette as any).primary[700]}>
                      {t("global.go_to_vehicle", "Go to vehicle")}
                    </Typography>
                  </Grid>
                  <StyledFontAwesomeIcon style={fontAwesomeOldXlStyle} icon={faArrowRight} />
                </StyledBox>
              </StyledBoxLink>
            </Grid>
          </ListItem>
        </List>
        {eventType === "Arbetsorder" && (
          <ListItem disableGutters>
            <ListItemButton
              onClick={() =>
                routerHistory.push(
                  allRoutes.IssuereportsWorkordersDetails(JSON.parse(event?.data).ReferenceId)
                )
              }>
              <ListItemAvatar>
                <Avatar>
                  <FontAwesomeIcon icon={faCarWrench as IconProp} />
                </Avatar>
              </ListItemAvatar>
              <ListItemText primary={t("global.manage_work_order", "Manage work order")} />
            </ListItemButton>
          </ListItem>
        )}
      </StyledDialog>
    </>
  )
}
